import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Menu, X, Globe } from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrandSettings } from '../lib/useBrandSettings';

interface HeaderProps {
  onOpenCart?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenCart }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const cartItems = useCartStore((state) => state.items);
  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const { brand } = useBrandSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: t('nav.home'), href: '#hero' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.painTech'), href: '#pain-tech' },
    { name: t('nav.principle'), href: '#principle' },
    { name: t('nav.products'), href: '#products' },
    { name: t('nav.testimonials'), href: '#experts' },
    { name: t('nav.cases'), href: '#cases' },
    { name: t('nav.locations'), href: '#locations' },
  ];

  const scrollToSection = (hash: string) => {
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleNavClick = (e: React.MouseEvent, hash: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (isHome) {
      scrollToSection(hash);
      // Update URL hash without reload
      window.history.replaceState(null, '', hash);
    } else {
      navigate(`/${hash}`);
      // Wait for navigation + render, then scroll
      setTimeout(() => scrollToSection(hash), 100);
    }
  };

  const langs = [
    { code: 'zh-HK', label: '繁' },
    { code: 'zh-CN', label: '简' },
    { code: 'en', label: 'EN' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-brand-paper/90 backdrop-blur-md border-b border-brand-gold/20 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo with brand name */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-3">
            <img
              src={brand.brandLogoUrl}
              alt={brand.brandName}
              className="h-12 w-auto object-contain"
            />
            <div className="flex flex-col leading-none transition-all duration-500">
              <span className={`text-2xl md:text-3xl font-serif font-bold text-brand-ink tracking-[0.15em] ${
                scrolled ? '' : '[filter:drop-shadow(0_0_1px_rgba(255,255,255,1))_drop-shadow(0_0_2px_rgba(255,255,255,0.95))_drop-shadow(0_0_3px_rgba(255,255,255,0.75))_drop-shadow(0_0_5px_rgba(255,255,255,0.5))]'
              }`}>
                {brand.brandName}
              </span>
              <span className="hidden md:inline text-[9px] font-sans tracking-[0.35em] text-brand-gold uppercase mt-0.5">
                {brand.brandNameEn} · SINCE {brand.brandSince}
              </span>
              <span className="md:hidden text-[9px] font-sans tracking-[0.3em] text-brand-gold uppercase mt-1">
                {brand.brandNameEn}
              </span>
              <span className="md:hidden text-[6px] font-sans tracking-[0.35em] text-brand-gold/80 uppercase mt-[3px] self-end">
                SINCE {brand.brandSince}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex space-x-7">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={isHome ? item.href : `/${item.href}`}
                onClick={(e) => handleNavClick(e, item.href)}
                className="relative text-brand-ink/75 hover:text-brand-cinnabar transition-colors font-sans text-sm tracking-wide after:absolute after:left-0 after:-bottom-1 after:h-[1px] after:w-0 after:bg-brand-cinnabar after:transition-all hover:after:w-full"
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center gap-1 border border-brand-ink/10 rounded-full px-1 py-0.5">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={`px-2.5 py-1 text-xs font-sans rounded-full transition-all ${
                    i18n.language === l.code
                      ? 'bg-brand-cinnabar text-brand-paper'
                      : 'text-brand-ink/60 hover:text-brand-ink'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => onOpenCart?.()}
              className="relative p-2 text-brand-ink hover:text-brand-cinnabar transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-brand-cinnabar text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-brand-ink"
              aria-label={isMenuOpen ? '關閉選單' : '開啟選單'}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - right side drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="lg:hidden fixed inset-0 z-40 bg-brand-ink/50 backdrop-blur-sm"
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="lg:hidden fixed top-0 right-0 z-50 w-[240px] max-w-[75vw] bg-brand-paper shadow-2xl border-l border-brand-gold/20 rounded-bl-2xl"
            >
              <div className="flex items-center justify-between px-4 h-16 border-b border-brand-ink/5">
                <span className="font-serif text-lg text-brand-ink tracking-widest">{t('nav.menu')}</span>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 text-brand-ink hover:text-brand-cinnabar"
                  aria-label="關閉選單"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="px-4 py-3 space-y-0.5">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={isHome ? item.href : `/${item.href}`}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="block py-2.5 text-brand-ink/80 hover:text-brand-cinnabar font-sans text-sm tracking-wide border-b border-brand-ink/5"
                  >
                    {item.name}
                  </a>
                ))}
                <div className="flex items-center gap-2 pt-4 pb-2">
                  <Globe className="w-4 h-4 text-brand-ink/50" />
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`px-3 py-1 text-xs font-sans rounded-full transition-all ${
                        i18n.language === l.code
                          ? 'bg-brand-cinnabar text-brand-paper'
                          : 'text-brand-ink/60 border border-brand-ink/10'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
